/**
 * Widget chrome tokens — pure TS constants consumed by:
 * - Launcher IIFE (must tree-shake to zero React)
 * - SPA (circuit-chat-embed)
 * - Preview (circuit-app)
 *
 * NO React, NO CSS imports, NO side effects.
 */

// ----- Sizing -----

/** Diameter of the launcher bubble in px. */
export const LAUNCHER_SIZE_PX = 64;

/** Icon size inside the launcher bubble in px. */
export const LAUNCHER_ICON_SIZE_PX = 32;

/** Distance from viewport edge to launcher bubble in px. */
export const LAUNCHER_INSET_PX = 24;

/** Gap between launcher bubble and iframe in windowed mode. */
export const LAUNCHER_GAP_PX = 16;

/** Default iframe width in px. */
export const IFRAME_WIDTH_PX = 375;

/** Default iframe height in px (re-exports from shared). */
export const IFRAME_HEIGHT_PX = 620;

/** Border radius for windowed panel chrome in px. */
export const PANEL_RADIUS_PX = 20;

/** Minimum iframe width in px. */
export const IFRAME_MIN_WIDTH_PX = 320;

/** Minimum iframe height in px (windowed style). */
export const IFRAME_MIN_HEIGHT_PX = 480;

/** Minimum iframe height in px (floating style). */
export const IFRAME_MIN_HEIGHT_PX_FLOATING = 120;

/** Maximum iframe width in px. */
export const IFRAME_MAX_WIDTH_PX = 1280;

/** Maximum iframe height in px. */
export const IFRAME_MAX_HEIGHT_PX = 1440;

/** Viewport margin around iframe in px. */
export const IFRAME_VIEWPORT_MARGIN_PX = 24;

// ----- Colors -----

/** Circuit brand green for the no-logo launcher bubble. */
export const BUBBLE_BG_COLOR = '#30a46c';

/** Neutral background for logo bubbles (shows behind transparent regions). */
export const BUBBLE_LOGO_BG_COLOR = '#ededed';

/** Windowed panel background color. */
export const PANEL_BG = '#F8F8F8';

/** Windowed panel border color. */
export const PANEL_BORDER = 'rgba(0, 0, 0, 0.09)';

/** Windowed panel shadow. */
export const PANEL_SHADOW = '0 0 32px rgba(0, 0, 0, 0.05)';

/** Unread badge background color. */
export const BADGE_COLOR = '#E5484D';

// ----- Placement types -----

/**
 * Launcher placement — 4 corners only.
 * (Re-exported type from protocol for convenience)
 */
export type CornerPosition =
  | 'bottom-left'
  | 'bottom-right'
  | 'top-left'
  | 'top-right';

// ----- Widget style -----

/**
 * Presentation variant for the chat panel.
 * - `windowed`: opaque bordered pane with a modal shadow.
 * - `floating`: transparent surface, no chrome (used inside the launcher iframe).
 *
 * Defined here (not in WidgetShell.tsx) so the launcher IIFE and protocol
 * layer can reference it without pulling in React.
 */
export type WidgetStyle = 'floating' | 'windowed';

// ----- BE-mirrored widget defaults -----

/**
 * Default widget placement when the chatbot has no explicit position set.
 * Mirrors the BE fallback in pkg/circuitapi so preview and deployed widget
 * render identically on unset fields.
 */
export const DEFAULT_PLACEMENT: CornerPosition = 'bottom-right';

/**
 * Default widget style when the chatbot has no explicit style set.
 * Mirrors the BE fallback in pkg/circuitapi.
 */
export const DEFAULT_STYLE: WidgetStyle = 'windowed';

/**
 * Default attribution visibility when the chatbot has no explicit value set.
 * Mirrors the BE fallback in pkg/circuitapi.
 */
export const DEFAULT_HIDE_ATTRIBUTION = false;

/**
 * Default open-by-default state when the chatbot has no explicit value set.
 * Mirrors the BE fallback in pkg/circuitapi.
 */
export const DEFAULT_OPEN_BY_DEFAULT = false;

// ----- Placement helpers -----

/**
 * CSS positioning for each corner. Used by launcher IIFE for the bubble
 * and iframe, and by preview for staging the widget in a mocked viewport.
 */
export function cornerAnchor(position: CornerPosition): {
  bottom: string;
  left: string;
  right: string;
  top: string;
} {
  const inset = `${LAUNCHER_INSET_PX}px`;
  switch (position) {
    case 'top-left':
      return { bottom: 'auto', left: inset, right: 'auto', top: inset };
    case 'top-right':
      return { bottom: 'auto', left: 'auto', right: inset, top: inset };
    case 'bottom-left':
      return { bottom: inset, left: inset, right: 'auto', top: 'auto' };
    case 'bottom-right':
    default:
      return { bottom: inset, left: 'auto', right: inset, top: 'auto' };
  }
}

/** Horizontal edge the launcher hugs; used for transform-origin. */
export function horizontalAnchor(position: CornerPosition): 'left' | 'right' {
  return position === 'top-left' || position === 'bottom-left'
    ? 'left'
    : 'right';
}

/** Vertical edge the launcher hugs; used for transform-origin. */
export function verticalAnchor(position: CornerPosition): 'bottom' | 'top' {
  return position === 'top-left' || position === 'top-right' ? 'top' : 'bottom';
}

/**
 * Sign of y-translate for open/close nudge animation.
 * Bottom-anchored: +1 (nudge down), top-anchored: -1 (nudge up).
 */
export function closeNudgeSignY(position: CornerPosition): -1 | 1 {
  return verticalAnchor(position) === 'top' ? -1 : 1;
}

/**
 * Coerce a wire placement string to one of the 4 supported corners.
 *
 * The BE `chatbot_position` enum accepts all 9 grid cells (top/middle/bottom
 * × left/center/right) for historical reasons, but the embed CSS only
 * handles corner placement. `middle-*` collapses to the bottom row (preserves
 * left/right hand); `*-center` collapses to the right column; unknown values
 * fall back to `bottom-right`.
 *
 * Pure token module — no side effects, tree-shakes cleanly for the launcher.
 */
export function normalizePlacement(value: string | undefined): CornerPosition {
  switch (value) {
    case 'top-left':
      return 'top-left';
    case 'top-right':
    case 'top-center':
      return 'top-right';
    case 'bottom-left':
    case 'middle-left':
      return 'bottom-left';
    case 'middle-right':
    case 'middle-center':
    case 'bottom-right':
    case 'bottom-center':
    case undefined:
    default:
      return 'bottom-right';
  }
}

// ----- Tinting -----

/**
 * Turn the agent's `primaryColor` into CSS custom properties that tint the
 * widget's accent surfaces (prompt border, focus ring, send button, thinking
 * indicator). Identity chrome (launcher bubble, header avatar) is Circuit-
 * branded and does NOT read these vars.
 *
 * Returns an object suitable for spreading into a React `style` prop.
 * Callers pass through `React.CSSProperties` — kept as a plain object here
 * so the tokens module stays React-free for launcher tree-shaking.
 */
export function tintStyle(
  primaryColor: string | null | undefined,
): Record<string, string> {
  if (!primaryColor) return {};
  return {
    '--cb-color': primaryColor,
    '--cui-accent-9': primaryColor,
  };
}
